import { useState, useEffect, useCallback } from 'react';
import { feedingApi, type Feeding } from '../services/api';

interface UseFeedingsReturn {
  feedings: Feeding[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addFeeding: (data: Parameters<typeof feedingApi.create>[0]) => Promise<Feeding>;
  deleteFeeding: (id: string) => Promise<void>;
}

interface UseFeedingsOptions {
  petId?: string;
  limit?: number;
  from?: number;
  to?: number;
}

export function useFeedings(options: UseFeedingsOptions = {}): UseFeedingsReturn {
  const [feedings, setFeedings] = useState<Feeding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeedings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await feedingApi.list({
        pet_id: options.petId,
        limit: options.limit,
        from: options.from,
        to: options.to,
      });
      setFeedings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [options.petId, options.limit, options.from, options.to]);

  useEffect(() => {
    fetchFeedings();
  }, [fetchFeedings]);

  const addFeeding = useCallback(async (data: Parameters<typeof feedingApi.create>[0]) => {
    const newFeeding = await feedingApi.create(data);
    setFeedings(prev => [newFeeding, ...prev]);
    return newFeeding;
  }, []);

  const deleteFeeding = useCallback(async (id: string) => {
    await feedingApi.delete(id);
    setFeedings(prev => prev.filter(f => f.id !== id));
  }, []);

  return {
    feedings,
    loading,
    error,
    refetch: fetchFeedings,
    addFeeding,
    deleteFeeding,
  };
}

// Hook to get today's feedings
export function useTodayFeedings(petId?: string): UseFeedingsReturn {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startOfDay = Math.floor(today.getTime() / 1000);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endOfDay = Math.floor(tomorrow.getTime() / 1000);

  return useFeedings({
    petId,
    from: startOfDay,
    to: endOfDay,
    limit: 100,
  });
}
